// ExploreSection.js
// "Explore" section: the lesson finder (flagged), and the STEM Missions /
// Summer Camp Journeys pill grids. Pills resolve journeys through
// useJourneyMap the same way StemMissionLauncher/SummerCampJourneyLauncher
// did. See docs/superpowers/specs/2026-09-16-home-redesign.md §5 (Student > ExploreSection).
import React from "react";
import { View, StyleSheet } from "react-native";

import HomeSection from "../primitives/HomeSection";
import HomeCard from "../primitives/HomeCard";
import HomePill from "../primitives/HomePill";
import useJourneyMap from "../hooks/useJourneyMap";
import LessonPlanLauncher from "../../lessonPlans/LessonPlanLauncher";
import { HOME } from "../homeTheme";
import journeyNote from "../journeyNote";

const ExploreSection = ({
  showLessonFinder = false,
  lessonFinderProps = {},
  showMissions = false,
  showJourneys = false,
  onOpenJourney,
  missionCatalog = [],
  dayCatalog = [],
  aside = null,
  headerZIndex = null,
  headerTestID = null,
  studentEmail = null,
  authToken = null,
  isDarkMode = false,
  t = (s) => s,
}) => {
  const { journeysBySlug, loading } = useJourneyMap({ studentEmail, authToken });

  // An entry is "live" when its slug resolves to a journey id and there is
  // somewhere to send the press. While the journey map is still loading,
  // nothing has resolved yet, so we keep showing every entry (unpressable)
  // rather than flashing the card away and back. Once loading finishes, a
  // catalog with no live entries renders no card at all -- a section with
  // nothing openable in it should not render (home-page gating fix, 2026-09-22).
  const isEntryLive = (item) => {
    const journey = journeysBySlug[item.slug] || null;
    return Number.isFinite(Number(journey?.id)) && typeof onOpenJourney === "function";
  };

  const visibleEntries = (catalog) => (loading ? catalog : catalog.filter(isEntryLive));

  const hasLiveEntries = (catalog) => catalog.some(isEntryLive);

  // What this render will actually show. Computed once so the "is there
  // anything to render at all" check below and the JSX below it can't drift.
  const willShowLessonFinder = showLessonFinder;
  const willShowMissions = showMissions && (loading || hasLiveEntries(missionCatalog));
  const willShowJourneys = showJourneys && (loading || hasLiveEntries(dayCatalog));

  const renderPills = (catalog, testIDPrefix) =>
    catalog.map((item) => {
      const journey = journeysBySlug[item.slug] || null;
      const available = Number.isFinite(Number(journey?.id)) && typeof onOpenJourney === "function";
      return (
        <HomePill
          key={item.slug}
          testID={`${testIDPrefix}-${item.slug}`}
          icon={item.icon}
          label={t(item.title || item.campTitle)}
          note={journeyNote(journey?.progress, t)}
          onPress={available ? () => onOpenJourney(Number(journey.id)) : null}
          isDarkMode={isDarkMode}
        />
      );
    });

  // Nothing to show: no lesson finder, no live (or still-resolving) missions,
  // no live (or still-resolving) journeys. Render nothing at all, including
  // the aside -- the aside only ever carried controls tied to what this
  // section shows. "See the student home" (the one control in it that was
  // useful regardless of missions/journeys) now lives in Quick actions
  // instead (see TeacherHomeScreen/QuickActions), so it survives this.
  if (!willShowLessonFinder && !willShowMissions && !willShowJourneys) {
    return null;
  }

  return (
    <View style={styles.root}>
      <HomeSection
        title={t("Explore")}
        aside={aside}
        headerZIndex={headerZIndex}
        headerTestID={headerTestID}
        isDarkMode={isDarkMode}
      >
        {willShowLessonFinder ? (
          <View style={styles.block}>
            <LessonPlanLauncher isDarkMode={isDarkMode} t={t} {...lessonFinderProps} />
          </View>
        ) : null}

        {willShowMissions ? (
          <View style={styles.block}>
            <HomeCard title={t("STEM Missions")} isDarkMode={isDarkMode} testID="explore-missions-card">
              <View style={styles.pillRow}>{renderPills(visibleEntries(missionCatalog), "mission-pill")}</View>
            </HomeCard>
          </View>
        ) : null}

        {willShowJourneys ? (
          <View style={styles.block}>
            <HomeCard title={t("Summer Camp Journeys")} isDarkMode={isDarkMode} testID="explore-journeys-card">
              <View style={styles.pillRow}>{renderPills(visibleEntries(dayCatalog), "day-pill")}</View>
            </HomeCard>
          </View>
        ) : null}
      </HomeSection>
    </View>
  );
};

const styles = StyleSheet.create({
  // Carries the margin the callers' wrapper <View> used to add around
  // ExploreSection, now living on ExploreSection's own root so that margin
  // disappears along with the section when there is nothing to show.
  root: { marginBottom: HOME.gap },
  block: { marginBottom: 12 },
  pillRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
});

export default ExploreSection;
